import { defineStore } from 'pinia';
import { debugLog } from '@/utils/logger'
import { ref, computed, watch } from 'vue';
import type { Household } from '@/models/Household';
import type { Child } from '@/models/Child';
import { householdService } from '@/services/household.service';
import { childService } from '@/services/child.service';
import { useAuthStore } from '@/stores/auth';
import { useRealtimeSync } from '@/composables/useRealtimeSync';
import { checkLevelUpChest } from '@/services/levelChest';
import { useAnalytics } from '@/composables/useAnalytics';
import { useCoinsStore } from '@/stores/coins';
import { isHouseholdPremium, hasPaidPremium } from '@/services/premium.service';
import { childHasAccess as childHasFullAccess, canAddChildForFree, canUseFeature as canUseFeatureFor } from '@/services/childAccess';
import { awayDaysService } from '@/services/awayDays.service';
import { eventBus } from '@/utils/eventBus';

export const useAppContextStore = defineStore('appContext', () => {
  // État global du household courant
  const currentHousehold = ref<Household | null>(null);
  const availableHouseholds = ref<Household[]>([]);
  const householdsLoading = ref(false);

  // État global de l'enfant courant
  const currentChild = ref<Child | null>(null);
  const availableChildren = ref<Child[]>([]);
  const childrenLoading = ref(false);

  // Child view mode (parent switches to child perspective) — persisted across reloads
  const storedChildViewChildId = localStorage.getItem('harmonia_child_view_child_id')
  const childViewMode = ref(!!storedChildViewChildId)
  const childViewChildId = ref<string | null>(storedChildViewChildId)

  /**
   * Le mode enfant ouvert par un parent sur SON appareil, pour une tablette
   * partagée (HARMONIA-87). Tant qu'il est vrai, rien ne sort de /child-mode
   * sans le code parent : le routeur y renvoie toute navigation (relance,
   * notification, lien, retour Android), et la sortie demande le code.
   *
   * Persisté avec l'enfant regardé : tuer l'app ne doit pas suffire à en
   * sortir. Les entrées admin et captures ne le posent pas, elles restent
   * libres comme avant.
   */
  const storedChildViewLocked =
    !!storedChildViewChildId && localStorage.getItem('harmonia_child_view_locked') === '1'
  const childViewLocked = ref(storedChildViewLocked)

  /**
   * Le téléphone de l'enfant, appairé à lui seul (#402, ADR 0004) : la session
   * est celle du parent, et le mode enfant y est verrouillé sur CET enfant, sans
   * changement d'enfant. Persisté avec le verrou ; la déconnexion le vide avec
   * le reste du stockage du compte (clearAccountStorage).
   */
  const PAIRED_DEVICE_KEY = 'harmonia_paired_device'
  function readPairedDevice(): { id: string; childId: string } | null {
    try {
      const raw = JSON.parse(localStorage.getItem(PAIRED_DEVICE_KEY) ?? 'null')
      return raw && typeof raw.id === 'string' && typeof raw.childId === 'string' ? raw : null
    } catch {
      return null
    }
  }
  const pairedDevice = ref<{ id: string; childId: string } | null>(readPairedDevice())

  function setPairedDevice(device: { id: string; childId: string }) {
    pairedDevice.value = device
    localStorage.setItem(PAIRED_DEVICE_KEY, JSON.stringify(device))
  }

  function enterChildMode(childId: string, opts: { locked?: boolean } = {}) {
    childViewMode.value = true
    childViewChildId.value = childId
    localStorage.setItem('harmonia_child_view_child_id', childId)
    // Le verrou se pose, il ne se lève jamais ici : changer d'enfant depuis le
    // sélecteur du mode enfant rappelle cette fonction, et une entrée admin
    // passée par-dessus ne doit pas ouvrir la porte.
    if (opts.locked) {
      childViewLocked.value = true
      localStorage.setItem('harmonia_child_view_locked', '1')
      syncPushTokenChildMode()
    }
  }

  // Le jeton push de l'appareil suit le verrou : marqué tant qu'il tient, pour
  // que les notifications parent ne partent pas vers un enfant. Import
  // dynamique : le service est natif et inutile au premier rendu.
  function syncPushTokenChildMode() {
    void import('@/services/push-token.service')
      .then((m) => m.registerCurrentToken())
      .catch(() => {})
  }

  /**
   * Lève le verrou SANS quitter le mode enfant. La sortie en a besoin : elle
   * change de section avant de vider le contexte enfant (cf.
   * useExitChildMode), et le routeur renverrait cette navigation vers
   * /child-mode si le verrou tenait encore.
   */
  function unlockChildMode() {
    const wasLocked = childViewLocked.value
    childViewLocked.value = false
    localStorage.removeItem('harmonia_child_view_locked')
    if (wasLocked) syncPushTokenChildMode()
  }

  function exitChildMode() {
    unlockChildMode()
    // Sortir du mode enfant avec le code rend ce téléphone à un usage parent :
    // il n'est plus « le téléphone de l'enfant ». La ligne paired_devices reste,
    // le parent la retire depuis la liste de ses téléphones.
    pairedDevice.value = null
    localStorage.removeItem(PAIRED_DEVICE_KEY)
    childViewMode.value = false
    childViewChildId.value = null
    localStorage.removeItem('harmonia_child_view_child_id')
  }

  // Realtime sync
  const realtimeSync = useRealtimeSync();
  const analytics = useAnalytics();
  let currentSyncHouseholdId: string | null = null;

  // Computed properties
  const hasCurrentHousehold = computed(() => currentHousehold.value !== null);
  const hasCurrentChild = computed(() => currentChild.value !== null);
  const currentHouseholdId = computed(() => currentHousehold.value?.id || null);
  const currentChildId = computed(() => currentChild.value?.id || null);
  // Accès aux fonctionnalités : abonnement OU essai en cours. Tout le gating
  // de l'app passe par là, l'essai n'a donc rien de spécial à câbler.
  const isPremium = computed(() => {
    if (!currentHousehold.value) return false
    return isHouseholdPremium(currentHousehold.value)
  });

  /**
   * « Cette fonctionnalité avancée est-elle ouverte ? »
   *
   * Un foyer `legacy` répond comme avant : il faut l'abonnement. Un foyer
   * `child_limit` les a toutes gratuitement, c'est le profil qui se monnaye
   * chez lui. C'est la question que doivent poser les points personnalisés,
   * les tâches bonus, les routines, l'argent de poche, l'espace enfant, les
   * séries et l'historique des bilans.
   *
   * `isPremium` juste au-dessus ne répond PAS à celle-là : il ne sert qu'aux
   * deux exceptions réservées aux abonnés dans les deux cohortes, les avatars
   * premium et les conseils de la chouette.
   */
  const canUseFeature = computed(() => canUseFeatureFor(currentHousehold.value));

  /**
   * « Ce profil enfant est-il ouvert ? »
   *
   * Toujours oui dans un foyer `legacy`, qui n'a jamais eu de limite. Dans un
   * foyer `child_limit`, le premier enfant, ou tous si le foyer est abonné.
   */
  const childHasAccess = (childId: string | null | undefined): boolean => {
    if (!childId) return false
    return childHasFullAccess(childId, availableChildren.value, currentHousehold.value)
  }

  /**
   * « Cette fonctionnalité avancée est-elle rendue POUR CET ENFANT ? »
   *
   * Il faut les deux : le profil ouvert ET la fonctionnalité ouverte. Miroir
   * exact de `child_feature_allowed()` en base, et la question que doit poser
   * toute surface qui gardait auparavant sur `isPremium` en visant un enfant.
   * Les quatre cas tombent juste sans que l'appelant connaisse les cohortes :
   * un foyer `legacy` gratuit refuse (son profil est ouvert mais pas la
   * fonctionnalité), un foyer `child_limit` gratuit accepte pour son premier
   * enfant seulement.
   */
  const childFeatureAllowed = (childId: string | null | undefined): boolean =>
    childHasAccess(childId) && canUseFeature.value

  /**
   * Les enfants que le mode enfant peut montrer : ceux qui y ont droit, la
   * même règle que l'espace enfant (garde `requiresPremium` de /child-mode).
   */
  const childModeChildren = computed(() =>
    availableChildren.value.filter((child) => childFeatureAllowed(child.id))
  )

  /**
   * L'enfant sur lequel ouvrir (ou garder) le mode enfant : celui demandé s'il
   * y a droit, sinon le premier qui y a droit, sinon personne. Le bouton
   * d'entrée et le verrou du routeur posent la même question.
   */
  const childModeTargetId = (preferred?: string | null): string | null => {
    if (preferred && childFeatureAllowed(preferred)) return preferred
    return childModeChildren.value[0]?.id ?? null
  }

  /**
   * La cohorte part dans la mesure dès que le foyer est connu.
   *
   * Sans elle, les deux modèles sont indistinguables dans Amplitude et on ne
   * peut pas répondre à la seule question qui justifie le changement : est-ce
   * que `child_limit` convertit mieux que `legacy` ?
   *
   * Posée par un watch et pas dans les setters : il y en a trois qui écrivent
   * `currentHousehold`, et le jour où un quatrième apparaît il n'y a rien à
   * penser. `immediate` couvre le foyer déjà chargé depuis le cache au boot.
   */
  watch(
    () => currentHousehold.value?.business_cohort,
    (cohort) => {
      if (!cohort) return
      // Best-effort, comme toute la mesure : une panne d'analytics ne doit
      // jamais empêcher un foyer de se charger.
      try {
        void analytics.setUserProperty?.('business_cohort', cohort)?.catch(() => {})
      } catch { /* la mesure n'est pas un chemin critique */ }
    },
    { immediate: true },
  )

  /** Les profils en pause, à montrer comme tels plutôt qu'à bloquer en silence. */
  const lockedChildren = computed(() =>
    availableChildren.value.filter((child) => !childHasAccess(child.id))
  )

  /** L'enfant affiché est-il ouvert ? Le raccourci que lisent les écrans enfant. */
  const currentChildHasAccess = computed(() => childHasAccess(currentChildId.value))

  /**
   * Un enfant de plus sans payer ? Toujours vrai dans un foyer `legacy` ;
   * faux dès le deuxième dans un foyer `child_limit` non abonné.
   */
  const canAddChild = computed(() =>
    canAddChildForFree(availableChildren.value, currentHousehold.value)
  )

  /** Abonné payant. Ne sert pas à ouvrir des portes, seulement à l'afficher. */
  const isPaidPremium = computed(() => {
    if (!currentHousehold.value) return false
    return hasPaidPremium(currentHousehold.value)
  });

  // Méthode pour pré-charger les données sans recharger depuis la DB
  const preloadContext = (household: Household, children: Child[]) => {
    // console.log('⚡ [AppContext] Pre-loading context with provided data...');
    // console.log('🏠 [AppContext] Household to preload:', household);
    // console.log('👶 [AppContext] Children to preload:', children);
    // console.log('🏠 [AppContext] Household ID:', household?.id);
    // console.log('👶 [AppContext] Children count:', children?.length || 0);

    // Vérifier que les données sont valides
    if (!household) {
      console.error('❌ [AppContext] No household provided for preloading');
      return;
    }

    if (!children || children.length === 0) {
      console.warn('⚠️ [AppContext] No children provided for preloading');
    }

    // Pré-charger directement sans recharger depuis la DB
    currentHousehold.value = household;
    availableHouseholds.value = [household];
    availableChildren.value = children;
    currentChild.value = children[0] || null;

    // Sauvegarder les listes dans localStorage (cache pour cold start)
    localStorage.setItem('harmonia_available_households', JSON.stringify([household]));
    localStorage.setItem('harmonia_available_children', JSON.stringify(children));

    // console.log('✅ [AppContext] Context pre-loaded successfully');
    // console.log('🏠 [AppContext] Current household set:', currentHousehold.value?.id);
    // console.log('👶 [AppContext] Available children set:', availableChildren.value.length);
    // console.log('👶 [AppContext] Current child set:', currentChild.value?.id);
  };

  // Actions pour gérer le household courant
  const setCurrentHousehold = async (household: Household | null) => {
    // console.log('🏠 [AppContext] setCurrentHousehold called:', {
    //   newHouseholdId: household?.id,
    //     newHouseholdName: household?.name,
    //       previousHouseholdId: currentHousehold.value?.id,
    //         timestamp: new Date().toISOString()
    // });

    const previousHouseholdId = currentHousehold.value?.id;
    currentHousehold.value = household;

    // Persister le foyer actif pour que le choix (switcher multi-foyers)
    // survive aux reboots — sinon loadUserHouseholds retombe sur le foyer
    // "primaire" (premier possédé).
    if (household) {
      localStorage.setItem('harmonia_current_household_id', household.id);
    } else {
      localStorage.removeItem('harmonia_current_household_id');
    }

    if (household) {
      // Charger automatiquement les enfants du nouveau foyer
      // console.log('👶 [AppContext] Loading children for new household');
      await loadHouseholdChildren(household.id);

      // Si on a changé de foyer, redémarrer la synchronisation realtime
      // MAIS seulement si on n'est pas sur la page d'onboarding (éviter les boucles)
      if (previousHouseholdId && previousHouseholdId !== household.id) {
        if (typeof window === 'undefined' || window.location.pathname !== '/onboarding') {
          debugLog('🔄 [AppContext] Household changed, restarting realtime sync');
          
          // Track household switched
          analytics.logHouseholdSwitched(previousHouseholdId, household.id);
          
          startAllRealtimeSync(household.id);
          // New household → re-hydrate its data into the stores (fresh, force).
          import('@/services/householdHydration').then(({ hydrateHousehold }) =>
            hydrateHousehold(household.id, { force: true }),
          ).catch((e) => console.error('[appContext] hydrate on switch failed:', e));
        } else {
          debugLog('⏸️ [AppContext] Household changed but on onboarding page, skipping realtime sync');
        }
      }
    } else {
      // Arrêter la synchronisation si on n'a plus de household
      stopAllRealtimeSync();

      // Vider les enfants disponibles si pas de foyer
      availableChildren.value = [];
      setCurrentChild(null);
      syncChildrenToStorage();
    }
  };

  const HOUSEHOLDS_CACHE_TTL_MS = 30_000 // 30s short TTL to reduce redundant calls
  let lastHouseholdsLoadUserId: string | null = null
  let lastHouseholdsLoadTime = 0
  let householdsLoadPromise: Promise<void> | null = null

  /** Call loadUserHouseholds only if not already loaded for this user (TTL). Use from pages/composables. */
  const ensureHouseholdsLoaded = async (userId: string): Promise<void> => {
    // If a load is already in progress, wait for it to complete before checking cache
    if (householdsLoadPromise) {
      await householdsLoadPromise
    }
    const now = Date.now()
    if (lastHouseholdsLoadUserId === userId && now < lastHouseholdsLoadTime + HOUSEHOLDS_CACHE_TTL_MS) {
      return
    }
    await loadUserHouseholds(userId)
  }

  const loadUserHouseholds = async (userId: string) => {
    const now = Date.now()
    if (householdsLoading.value) {
      // Wait for the in-progress load instead of silently returning
      if (householdsLoadPromise) await householdsLoadPromise
      return
    }
    if (lastHouseholdsLoadUserId === userId && now < lastHouseholdsLoadTime + HOUSEHOLDS_CACHE_TTL_MS) return

    const doLoad = async () => {
      try {
        householdsLoading.value = true;
        lastHouseholdsLoadUserId = userId
        lastHouseholdsLoadTime = now

        const households = await householdService.getUserHouseholds(userId);
        availableHouseholds.value = households;
        localStorage.setItem('harmonia_available_households', JSON.stringify(households));

        let householdToLoad = currentHousehold.value;

        // Restaurer le foyer actif choisi précédemment (switcher) avant de
        // retomber sur le foyer primaire.
        if (!householdToLoad) {
          const storedId = localStorage.getItem('harmonia_current_household_id');
          const stored = storedId ? households.find(h => h.id === storedId) : undefined;
          if (stored) {
            await setCurrentHousehold(stored);
            householdToLoad = stored;
          }
        }

        if (!householdToLoad && households.length > 0) {
          const primaryHousehold = await householdService.getUserHousehold(userId);
          if (primaryHousehold) {
            await setCurrentHousehold(primaryHousehold);
            householdToLoad = primaryHousehold;
          }
        } else if (householdToLoad) {
          const stillValid = households.find(h => h.id === householdToLoad!.id);
          if (stillValid) {
            await loadHouseholdChildren(householdToLoad.id);
          } else {
            if (households.length > 0) {
              await setCurrentHousehold(households[0]);
            } else {
              setCurrentHousehold(null);
            }
          }
        }

        if (currentHousehold.value && (typeof window === 'undefined' || window.location.pathname !== '/onboarding')) {
          debugLog('🔄 [AppContext] Starting realtime sync for household:', currentHousehold.value.id)
          startAllRealtimeSync(currentHousehold.value.id)
        } else if (currentHousehold.value && typeof window !== 'undefined' && window.location.pathname === '/onboarding') {
          debugLog('⏸️ [AppContext] Skipping realtime sync - on onboarding page')
        }
      } catch (error) {
        console.error('❌ [AppContext] Error loading households:', error);
      } finally {
        householdsLoading.value = false;
        householdsLoadPromise = null;
      }
    }

    householdsLoadPromise = doLoad()
    await householdsLoadPromise
  };

  // Actions pour gérer l'enfant courant
  const setCurrentChild = (child: Child | null) => {
    // console.log('👶 [AppContext] setCurrentChild called:', {
    // newChildId: child?.id,
    //     newChildName: child?.name,
    //       newChildPoints: child?.points,
    //         currentChildId: currentChild.value?.id,
    //           currentChildPoints: currentChild.value?.points,
    //             timestamp: new Date().toISOString()
    // });

    const previousChildId = currentChild.value?.id;

    // Éviter les mises à jour inutiles si l'enfant est déjà le même
    if (currentChild.value && child && currentChild.value.id === child.id) {
      const sameName = currentChild.value.name === child.name;
      const samePoints = currentChild.value.points === child.points;
      const sameColor = (currentChild.value.color || null) === (child.color || null);
      if (sameName && samePoints && sameColor) {
        // console.log('🏪 [AppContext] Skipping setCurrentChild - same data already set');
        return; // Skip silently
      }
    }

    // console.log('🏪 [AppContext] setCurrentChild called', {
    //   newChildId: child?.id,
    //     newChildName: child?.name,
    //       newChildPoints: child?.points,
    //         oldChildId: currentChild.value?.id,
    //           oldChildName: currentChild.value?.name,
    //             oldChildPoints: currentChild.value?.points
    // });

    // Réinitialiser les coins tout de suite pour ne pas afficher le solde de l'enfant précédent
    if (previousChildId !== child?.id) {
      const coinsStore = useCoinsStore();
      coinsStore.resetBalance();
    }

    currentChild.value = child;

    if (child) {
      // Track child selected if different from previous
      if (previousChildId && previousChildId !== child.id) {
        analytics.logChildSelected(child.id, previousChildId);
      } else if (!previousChildId) {
        analytics.logChildSelected(child.id);
      }
      
      // Load coin balance for the selected child (non-critical).
      const coinsStore = useCoinsStore();
      void (async () => {
        try {
          // maxAgeMs : ce passage est aussi emprunté après chaque validation,
          // quand le solde vient d'être relu (HARMONIA-77).
          await coinsStore.loadBalance(child.id, { maxAgeMs: 3000 });
        } catch (balanceError: any) {
          console.warn(`⚠️ [AppContext] Failed to load coin balance (non-critical):`, balanceError.message || balanceError);
        }
      })();

      // console.log('✅ [AppContext] New current child set in store', {
      //   childId: child.id,
      //     name: child.name,
      //       points: child.points
      // });
    } else {
      // Reset coin balance when no child selected
      const coinsStore = useCoinsStore();
      coinsStore.resetBalance();
      // console.log('✅ [AppContext] Current child cleared from store');
    }
  };

  /**
   * Applique de nouveaux points à UN enfant précis, sélectionné ou non, et
   * déclenche la détection de niveau pour lui.
   *
   * Le point d'entrée historique ne connaissait que l'enfant courant : valider
   * une tâche depuis la vue « Tous » écrivait les points d'un enfant dans
   * l'objet d'un autre, et le coffre de palier était attribué au mauvais enfant
   * ou pas du tout.
   */
  const updateChildPoints = (childId: string, newPoints: number) => {
    if (!childId) return;

    const index = availableChildren.value.findIndex(c => c.id === childId);
    const isCurrent = currentChild.value?.id === childId;
    const oldPoints = Number(
      (isCurrent ? currentChild.value?.points : availableChildren.value[index]?.points) ?? 0
    );

    if (oldPoints === newPoints) return;

    if (isCurrent && currentChild.value) currentChild.value.points = newPoints;
    if (index !== -1) {
      availableChildren.value[index] = { ...availableChildren.value[index], points: newPoints };
    }

    // Détection du passage de niveau (le coffre et sa notification suivent).
    // La logique vit dans `levelChest` et non ici : elle doit valoir pour
    // n'importe quel enfant, pas seulement celui que ce store regarde.
    setTimeout(() => checkLevelUpChest(childId, oldPoints, newPoints), 100);
  };

  const updateCurrentChildPoints = (newPoints: number) => {
    if (currentChild.value) updateChildPoints(currentChild.value.id, newPoints);
  };

  // Méthode ultra-rapide pour l'animation optimiste (sans logs)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateCurrentChildPointsOptimistic = (newPoints: number) => {
    if (currentChild.value) {
      // Don't update points optimistically if we want to detect level ups
      // The real update will come from updateCurrentChildPoints
      // currentChild.value.points = newPoints;
      // Pas de localStorage pour l'animation optimiste
    }
  };

  /**
   * Met à jour un enfant dans availableChildren et synchronise avec localStorage
   * Cette fonction est essentielle pour maintenir la cohérence du cache après une modification
   */
  const updateChildInAvailableChildren = (updatedChild: Child) => {
    // console.log('🔄 [AppContext] updateChildInAvailableChildren called:', {
    //   childId: updatedChild.id,
    //   childName: updatedChild.name,
    //   childColor: updatedChild.color
    // });

    // Mettre à jour l'enfant dans availableChildren
    const index = availableChildren.value.findIndex(c => c.id === updatedChild.id);
    if (index !== -1) {
      // Remplacer l'enfant dans le tableau
      availableChildren.value[index] = updatedChild;

      // Synchroniser avec localStorage
      localStorage.setItem('harmonia_available_children', JSON.stringify(availableChildren.value));

      // console.log('✅ [AppContext] Child updated in availableChildren');
    } else {
      // console.log('⚠️ [AppContext] Child not found in availableChildren');
    }

    // Si c'est l'enfant courant, le mettre à jour aussi
    if (currentChild.value && currentChild.value.id === updatedChild.id) {
      // console.log('🔄 [AppContext] Also updating currentChild');
      setCurrentChild(updatedChild);
    }
  };

  const loadHouseholdChildren = async (householdId: string) => {
    // console.log('👶 [AppContext] loadHouseholdChildren called for household:', householdId);
    try {
      childrenLoading.value = true;
      // console.log('🔄 [AppContext] Loading children...');

      // Charger tous les enfants du household courant
      const children = await childService.getChildrenByHouseholdId(householdId);
      // console.log('✅ [AppContext] Loaded children:', children.length);

      availableChildren.value = children;
      localStorage.setItem('harmonia_available_children', JSON.stringify(children));

      // Logique de sélection de l'enfant courant avec priorité à la persistance :
      // 1. Vérifier d'abord si l'enfant courant restauré depuis localStorage est valide
      const storedChild = currentChild.value;
      const currentChildStillValid = storedChild && children.find(c => c.id === storedChild.id);

      // console.log('🔍 [AppContext] Child validation:', {
      //   storedChildId: storedChild?.id,
      //     storedChildName: storedChild?.name,
      //       currentChildStillValid,
      //       availableChildrenCount: children.length
      // });

      if (currentChildStillValid) {
        // console.log('✅ [AppContext] Current child is still valid, updating data');
        // L'enfant restauré est valide, on le garde et on met à jour ses données
        const updatedChild = children.find(c => c.id === storedChild.id);
        if (updatedChild) {
          // Only preserve points if the stored points are higher than database points
          // This prevents stale data from overriding fresh database updates
          const currentPoints = storedChild.points || 0;
          const databasePoints = updatedChild.points || 0;
          const finalPoints = Math.max(currentPoints, databasePoints);

          // console.log('📊 [AppContext] Points comparison:', {
          //   storedPoints: currentPoints,
          //     databasePoints: databasePoints,
          //       finalPoints: finalPoints
          // });

          const updatedChildWithPoints = {
            ...updatedChild,
            points: finalPoints
          };

          // Éviter les mises à jour inutiles
          if (
            storedChild.points !== finalPoints ||
            storedChild.name !== updatedChild.name ||
            (storedChild.color || null) !== (updatedChild.color || null)
          ) {
            // console.log('🔄 [AppContext] Updating child with new data');
            setCurrentChild(updatedChildWithPoints);
          } else {
            // console.log('🏪 [AppContext] Skipping setCurrentChild - same data already set');
          }
        }
      } else if (children.length > 0) {
        // console.log('👶 [AppContext] No valid current child, selecting first available');
        // Pas d'enfant valide, prendre le premier disponible
        // Éviter les mises à jour si l'enfant est déjà le même
        if (!currentChild.value || currentChild.value.id !== children[0].id) {
          // console.log('🔄 [AppContext] Setting first child as current');
          setCurrentChild(children[0]);
        } else {
          // console.log('🏪 [AppContext] First child already selected');
        }
      } else {
        // console.log('❌ [AppContext] No children available, clearing current child');
        // Aucun enfant disponible
        if (currentChild.value !== null) {
          setCurrentChild(null);
        }
      }
    } catch (error) {
      console.error('❌ [AppContext] Error loading children:', error);
      if (error instanceof Error) {
        console.error('Error details:', error.message);
        console.error('Error stack:', error.stack);
      }
      availableChildren.value = [];
      setCurrentChild(null);
      syncChildrenToStorage();
    } finally {
      childrenLoading.value = false;
      // console.log('✅ [AppContext] loadHouseholdChildren completed');
    }
  };

  // Sync listes vers localStorage (appelé après toute mutation)
  const syncChildrenToStorage = () => {
    try {
      localStorage.setItem('harmonia_available_children', JSON.stringify(availableChildren.value));
    } catch (e) {
      console.warn('[AppContext] syncChildrenToStorage failed', e);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const syncHouseholdsToStorage = () => {
    try {
      localStorage.setItem('harmonia_available_households', JSON.stringify(availableHouseholds.value));
    } catch (e) {
      console.warn('[AppContext] syncHouseholdsToStorage failed', e);
    }
  };

  // Initialisation depuis localStorage (affichage instantané au retour sur l'app)
  const initializeFromStorage = () => {
    try {
      const storedHouseholds = localStorage.getItem('harmonia_available_households');
      if (storedHouseholds) {
        availableHouseholds.value = JSON.parse(storedHouseholds);
      }
      const storedChildren = localStorage.getItem('harmonia_available_children');
      if (storedChildren) {
        availableChildren.value = JSON.parse(storedChildren);
      }
    } catch (error) {
      console.error('Erreur lors de la restauration du contexte:', error);
      localStorage.removeItem('harmonia_available_households');
      localStorage.removeItem('harmonia_available_children');
    }
  };

  // Force le rechargement du contexte pour un utilisateur donné
  const forceReloadContext = async (userId: string) => {
    // console.log('[AppContext] Force reloading context for user:', userId);

    // Invalider le cache pour forcer un vrai rechargement (sinon loadUserHouseholds early-return)
    lastHouseholdsLoadUserId = null;
    lastHouseholdsLoadTime = 0;

    // Réinitialiser d'abord
    currentHousehold.value = null;
    currentChild.value = null;
    availableHouseholds.value = [];
    availableChildren.value = [];

    // Recharger tout le contexte
    await loadUserHouseholds(userId);

    // console.log('[AppContext] Context reloaded - household:', currentHousehold.value?.name, 'child:', currentChild.value?.name);
  };

  // Réinitialiser le contexte (lors de la déconnexion par exemple). Invalide le cache households.
  const resetContext = () => {
    stopAllRealtimeSync();
    exitChildMode(); // clear any lingering child-view selection (stale alias guard)
    lastHouseholdsLoadUserId = null;
    lastHouseholdsLoadTime = 0;

    currentHousehold.value = null;
    currentChild.value = null;
    availableHouseholds.value = [];
    availableChildren.value = [];
    // Jours d'absence : cache mémoire indexé par child_id. Sans purge, un
    // changement de compte laisserait les absences de l'ancien foyer visibles
    // au suivant (même piège que le token push réclamé à tort).
    awayDaysService.reset();
    localStorage.removeItem('harmonia_available_households');
    localStorage.removeItem('harmonia_available_children');
    localStorage.removeItem('harmonia_current_household_id');
  };

  // Watcher pour détecter la perte d'enfant et le récupérer automatiquement
  watch(currentChild, async (newChild, oldChild) => {
    // Si on avait un enfant et qu'on l'a perdu, mais qu'on est sur une page d'enfant
    if (oldChild && !newChild && window.location.pathname.includes('/children/')) {
      // console.log('⚠️ [AppContext] Child lost, attempting recovery...');
      // console.log('👶 [AppContext] Lost child:', oldChild.name, 'on URL:', window.location.pathname);

      // Récupérer l'enfant depuis l'URL
      const recovered = await recoverChildFromURL();
      if (recovered) {
        // console.log('✅ [AppContext] Child recovered after loss');
      } else {
        // console.log('❌ [AppContext] Failed to recover child after loss');
      }
    }
  }, { immediate: false });

  // Fonction pour récupérer l'enfant depuis l'URL
  const recoverChildFromURL = async () => {
    // console.log('🔄 [AppContext] Attempting to recover child from URL...');

    try {
      const currentPath = window.location.pathname;

      // Support pour l'ancien format: /children/{childId}
      let childMatch = currentPath.match(/\/children\/([^/]+)/);

      // Support pour le nouveau format: /h/{householdId}/c/{childId}
      if (!childMatch) {
        childMatch = currentPath.match(/\/h\/[^/]+\/c\/([^/]+)/);
      }

      if (childMatch && childMatch[1]) {
        const childId = childMatch[1];
        // console.log('👶 [AppContext] Found child ID in URL:', childId);

        // Vérifier si l'enfant est déjà dans les enfants disponibles
        const existingChild = availableChildren.value.find(c => c.id === childId);

        if (existingChild) {
          // console.log('✅ [AppContext] Child found in available children, setting as current');
          setCurrentChild(existingChild);

          // Émettre un événement pour forcer le rechargement des données après un délai
          setTimeout(() => {
            eventBus.emit('app:childRecovered', { childId, child: existingChild });
          }, 100);
          return true;
        } else {
          // console.log('🔄 [AppContext] Child not in available children, loading from service...');

          // Charger l'enfant depuis le service
          const childData = await childService.getChildById(childId);
          if (childData) {
            // console.log('✅ [AppContext] Child loaded from service, setting as current');
            setCurrentChild(childData);

            // Émettre un événement pour forcer le rechargement des données après un délai
            setTimeout(() => {
              eventBus.emit('app:childRecovered', { childId, child: childData });
            }, 100);
            return true;
          }
        }
      }

      // console.log('⚠️ [AppContext] No valid child ID found in URL');
      return false;
    } catch (error) {
      console.error('❌ [AppContext] Error recovering child from URL:', error);
      return false;
    }
  };

  // Fonction pour récupérer le contexte quand l'onglet reprend le focus
  const recoverContextOnFocus = async () => {
    // console.log('🔄 [AppContext] Recovering context on focus...');

    try {
      // Si on est sur une page d'enfant (ancien ou nouveau format), vérifier le contexte
      if (window.location.pathname.includes('/children/') || window.location.pathname.includes('/h/')) {
        // console.log('🔄 [AppContext] On child page, checking child context...');

        // Extraire l'ID de l'enfant de l'URL
        const currentPath = window.location.pathname;
        let childMatch = currentPath.match(/\/children\/([^/]+)/);
        if (!childMatch) {
          childMatch = currentPath.match(/\/h\/[^/]+\/c\/([^/]+)/);
        }

        if (childMatch && childMatch[1]) {
          const urlChildId = childMatch[1];

          // Vérifier si l'enfant courant correspond à celui de l'URL
          if (!currentChild.value || currentChild.value.id !== urlChildId) {
            // console.log('⚠️ [AppContext] Child mismatch or missing, recovering from URL...');
            const recovered = await recoverChildFromURL();
            if (recovered) {
              // console.log('✅ [AppContext] Child recovered from URL');
              return;
            }
          } else {
            // console.log('✅ [AppContext] Current child matches URL:', currentChild.value.name);
          }
        }
      }

      // Si on a perdu le household mais pas l'enfant
      if (!currentHousehold.value && currentChild.value) {
        // console.log('🏠 [AppContext] Household lost but child exists, reloading household...');
        await loadUserHouseholds(useAuthStore().user?.id || '');
      }

      // Recharger les données de l'enfant courant si nécessaire
      if (currentChild.value && currentHousehold.value) {
        // console.log('🔄 [AppContext] Refreshing current child data...');
        await loadHouseholdChildren(currentHousehold.value.id);
      }

      // console.log('✅ [AppContext] Context recovery completed');
    } catch (error) {
      console.error('❌ [AppContext] Error recovering context:', error);
    } finally {
      // console.log('🏁 [AppContext] Context recovery process finished');
    }
  };

  // Fonction pour recharger les données de la page courante
  const refreshCurrentPageData = async () => {
    // console.log('🔄 [AppContext] Refreshing current page data...');

    try {
      const currentPath = window.location.pathname;

      // Si on est sur une page d'enfant, recharger les données spécifiques
      if (currentPath.includes('/children/') || currentPath.includes('/h/')) {
        // console.log('🔄 [AppContext] On child page, refreshing child data...');

        // Recharger les enfants du household
        if (currentHousehold.value) {
          await loadHouseholdChildren(currentHousehold.value.id);
        }

        eventBus.emit('app:refreshPageData', {
          page: 'child',
          childId: currentChild.value?.id,
          householdId: currentHousehold.value?.id
        });
      }

      // console.log('✅ [AppContext] Page data refresh completed');
    } catch (error) {
      console.error('❌ [AppContext] Error refreshing page data:', error);
    }
  };

  // Démarre toutes les synchronisations realtime pour un household
  function startAllRealtimeSync(householdId: string) {
    // Ne pas démarrer la sync realtime si on est sur la page d'onboarding (éviter les boucles)
    if (typeof window !== 'undefined' && window.location.pathname === '/onboarding') {
      debugLog('[AppContext] On onboarding page, skipping ALL realtime sync to avoid loops')
      return
    }
    
    debugLog('[AppContext] Starting ALL realtime sync for household:', householdId)

    // Démarrer la sync du store AppContext (enfants)
    startRealtimeSync(householdId)

    // Forcer l'instanciation et le démarrage des autres stores
    // en important et appelant directement leurs méthodes
    // Cela garantit que les subscriptions sont créées même si les stores ne sont pas encore utilisés
    setTimeout(() => {
      // Import et init du store tasks
      import('./tasks').then(({ useTasksStore }) => {
        const tasksStore = useTasksStore()
        if (tasksStore && typeof tasksStore.startRealtimeSync === 'function') {
          tasksStore.startRealtimeSync(householdId)
          debugLog('[AppContext] Forced tasks store realtime sync')
        }
      }).catch(err => console.error('[AppContext] Error loading tasks store:', err))

      // Import et init du store rewards
      import('./rewards').then(({ useRewardsStore }) => {
        const rewardsStore = useRewardsStore()
        if (rewardsStore && typeof rewardsStore.startRealtimeSync === 'function') {
          rewardsStore.startRealtimeSync(householdId)
          debugLog('[AppContext] Forced rewards store realtime sync')
        }
      }).catch(err => console.error('[AppContext] Error loading rewards store:', err))

      // Note: Children store realtime sync est désactivé pour éviter les doublons
      // AppContext gère déjà la synchronisation des children via appcontext-children channel
    }, 100)
  }

  // Arrête toutes les synchronisations realtime
  function stopAllRealtimeSync() {
    debugLog('[AppContext] Stopping ALL realtime sync')

    stopRealtimeSync()

    // Arrêter aussi les autres stores s'ils sont instanciés.
    // Les stores eux-mêmes sont idempotents si rien n'est actif.
    setTimeout(() => {
      import('./tasks').then(({ useTasksStore }) => {
        const tasksStore = useTasksStore()
        if (tasksStore && typeof tasksStore.stopRealtimeSync === 'function') {
          tasksStore.stopRealtimeSync()
        }
      }).catch(() => { })

      import('./rewards').then(({ useRewardsStore }) => {
        const rewardsStore = useRewardsStore()
        if (rewardsStore && typeof rewardsStore.stopRealtimeSync === 'function') {
          rewardsStore.stopRealtimeSync()
        }
      }).catch(() => { })

      // Note: Children store realtime sync est désactivé pour éviter les doublons
    }, 100)
  }

  // Realtime synchronization pour les enfants
  function startRealtimeSync(householdId: string) {
    // Éviter de créer plusieurs subscriptions pour le même household
    if (currentSyncHouseholdId === householdId) {
      debugLog('[AppContext] Already syncing this household, skipping')
      return
    }
    
    // Ne pas démarrer la sync realtime si on est sur la page d'onboarding (éviter les boucles)
    if (typeof window !== 'undefined' && window.location.pathname === '/onboarding') {
      debugLog('[AppContext] On onboarding page, skipping realtime sync to avoid loops')
      return
    }

    // Arrêter les anciennes subscriptions si on change de household
    if (currentSyncHouseholdId) {
      stopRealtimeSync()
    }

    currentSyncHouseholdId = householdId
    debugLog('[AppContext] Starting realtime sync for household:', householdId)

    // Synchroniser la table children pour mettre à jour currentChild et availableChildren
    realtimeSync.subscribeToTable<Child>(
      `appcontext-children-${householdId}`,
      'children',
      {
        onInsert: (payload) => {
          const newChild = payload.new as Child
          // Éviter les doublons
          if (!availableChildren.value.find(c => c.id === newChild.id)) {
            availableChildren.value.push(newChild)
            // Synchroniser avec localStorage
            localStorage.setItem('harmonia_available_children', JSON.stringify(availableChildren.value))
            debugLog('[AppContext] Child inserted via realtime:', newChild.name)
          }
        },
        onUpdate: (payload) => {
          debugLog('[AppContext] onUpdate callback called for children')
          const updatedChild = payload.new as Child
          debugLog('[AppContext] Updated child:', updatedChild.id, updatedChild.name, 'color:', updatedChild.color)

          // Si c'est l'enfant courant, vérifier et mettre à jour AVANT de modifier availableChildren
          // pour éviter les problèmes de référence d'objets
          const needsUpdate = currentChild.value && currentChild.value.id === updatedChild.id
          debugLog('[AppContext] Needs update?', needsUpdate, 'Current child:', currentChild.value?.id)

          // Convertir en nombre pour la comparaison (au cas où il y aurait des différences de type)
          const currentPoints = Number(currentChild.value?.points ?? 0)
          const updatedPoints = Number(updatedChild.points ?? 0)
          const pointsChanged = needsUpdate && currentPoints !== updatedPoints
          
          const infoChanged = needsUpdate && (
            currentChild.value!.name !== updatedChild.name ||
            (currentChild.value!.color || null) !== (updatedChild.color || null) ||
            (currentChild.value!.avatar || null) !== (updatedChild.avatar || null)
          )

          debugLog('[AppContext] Points changed?', pointsChanged, 'Info changed?', infoChanged)
          debugLog('[AppContext] Points comparison:', {
            currentPoints: currentChild.value?.points,
            currentPointsNumber: currentPoints,
            updatedPoints: updatedChild.points,
            updatedPointsNumber: updatedPoints,
            areEqual: currentPoints === updatedPoints,
            typeCurrent: typeof currentChild.value?.points,
            typeUpdated: typeof updatedChild.points
          })

          // Mettre à jour dans availableChildren
          const index = availableChildren.value.findIndex(c => c.id === updatedChild.id)
          const previousPoints = index !== -1 ? Number(availableChildren.value[index]?.points ?? 0) : null
          if (index !== -1) {
            availableChildren.value[index] = updatedChild
            // Synchroniser avec localStorage
            localStorage.setItem('harmonia_available_children', JSON.stringify(availableChildren.value))
          }

          // Passage de niveau d'un enfant NON sélectionné : c'est le seul endroit
          // qui le voit (la validation vient d'un autre appareil ou de la vue
          // « Tous »). Sans ça son coffre n'était jamais généré. L'enfant courant
          // passe par updateCurrentChildPoints juste en dessous, et la garde
          // anti-doublon de levelChest absorbe le recouvrement.
          if (!needsUpdate && previousPoints !== null && previousPoints !== updatedPoints) {
            checkLevelUpChest(updatedChild.id, previousPoints, updatedPoints)
          }

          // Si c'est l'enfant courant, mettre à jour
          if (needsUpdate) {
            // IMPORTANT: Si les points ont changé, utiliser updateCurrentChildPoints pour déclencher la détection de niveau
            if (pointsChanged) {
              debugLog('[AppContext] Child points updated via realtime:', updatedChild.name, updatedChild.points)
              updateCurrentChildPoints(updatedChild.points)
              
              // Mettre à jour aussi les autres propriétés si elles ont changé
              if (infoChanged) {
                // Mettre à jour les infos sans écraser les points (déjà mis à jour)
                if (currentChild.value) {
                  currentChild.value.name = updatedChild.name
                  currentChild.value.color = updatedChild.color
                  currentChild.value.avatar = updatedChild.avatar
                }
                debugLog('[AppContext] Child info also updated via realtime:', updatedChild.name)
              }
            } else if (infoChanged) {
              // Si seulement les infos ont changé, mettre à jour sans déclencher la détection de niveau
              debugLog('[AppContext] Child info updated via realtime:', updatedChild.name)
              setCurrentChild(updatedChild)
            } else {
              // Si ni les points ni les infos n'ont changé mais que c'est quand même une mise à jour,
              // forcer quand même la mise à jour pour être sûr (cas edge)
              currentChild.value = updatedChild
              debugLog('[AppContext] Child fully updated via realtime:', updatedChild.name)
            }
          }

          // Keep the coins store in sync when children.coins changes (multi-user:
          // a coin awarded/spent on another device lands here).
          if (updatedChild?.id && (updatedChild.coins != null || updatedChild.points != null)) {
            useCoinsStore().setBalance(
              updatedChild.id,
              Number(updatedChild.coins ?? updatedChild.points ?? 0),
            )
          }
        },
        onDelete: (payload) => {
          const deletedChild = payload.old as Child

          // Retirer de availableChildren
          availableChildren.value = availableChildren.value.filter(c => c.id !== deletedChild.id)
          localStorage.setItem('harmonia_available_children', JSON.stringify(availableChildren.value))

          // Si c'est l'enfant courant, le désélectionner
          if (currentChild.value && currentChild.value.id === deletedChild.id) {
            // Sélectionner le premier enfant disponible ou null
            if (availableChildren.value.length > 0) {
              setCurrentChild(availableChildren.value[0])
            } else {
              setCurrentChild(null)
            }
          }

          debugLog('[AppContext] Child deleted via realtime:', deletedChild.id)
        }
      },
      `household_id=eq.${householdId}`
    )
  }

  function stopRealtimeSync() {
    if (currentSyncHouseholdId === null) return
    debugLog('[AppContext] Stopping realtime sync')
    realtimeSync.unsubscribeAll()
    currentSyncHouseholdId = null
  }

  // Sync state from URL params without persisting to localStorage
  // Direction: URL → store only. Called by useURLState watcher.
  const syncFromURL = (type: 'household' | 'child', id: string) => {
    if (type === 'household' && id !== currentHouseholdId.value) {
      const household = availableHouseholds.value.find(h => h.id === id)
      if (household) {
        currentHousehold.value = household
        loadHouseholdChildren(id)
      }
    }
    if (type === 'child' && id !== currentChildId.value) {
      const child = availableChildren.value.find(c => c.id === id)
      if (child) {
        currentChild.value = child
      }
    }
  }

  // Initialiser automatiquement depuis le localStorage
  initializeFromStorage();

  return {
    // État
    currentHousehold,
    availableHouseholds,
    householdsLoading,
    currentChild,
    availableChildren,
    childrenLoading,

    // Computed
    hasCurrentHousehold,
    hasCurrentChild,
    currentHouseholdId,
    currentChildId,
    isPremium,
    canUseFeature,
    childFeatureAllowed,
    childModeChildren,
    childModeTargetId,
    childHasAccess,
    currentChildHasAccess,
    lockedChildren,
    canAddChild,
    isPaidPremium,

    // Actions
    preloadContext,
    setCurrentHousehold,
    loadUserHouseholds,
    ensureHouseholdsLoaded,
    setCurrentChild,
    updateChildPoints,
    updateCurrentChildPoints,
    updateCurrentChildPointsOptimistic,
    updateChildInAvailableChildren,
    loadHouseholdChildren,
    initializeFromStorage,
    forceReloadContext,
    resetContext,
    recoverChildFromURL,
    recoverContextOnFocus,
    refreshCurrentPageData,
    syncFromURL,

    // Child view mode
    childViewMode,
    childViewChildId,
    childViewLocked,
    enterChildMode,
    unlockChildMode,
    exitChildMode,
    pairedDevice,
    setPairedDevice,

    // Realtime sync
    startRealtimeSync,
    stopRealtimeSync,
    startAllRealtimeSync,
    stopAllRealtimeSync
  };
});
